/* eslint-disable */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { InertiaPlugin } from 'gsap/dist/InertiaPlugin';
import { MotionPathPlugin } from 'gsap/dist/MotionPathPlugin';
import { MotionPathHelper } from 'gsap/dist/MotionPathHelper';
import { Physics2DPlugin } from 'gsap/dist/Physics2DPlugin';
import { ScrollSmoother } from 'gsap/dist/ScrollSmoother';
import { MorphSVGPlugin } from 'gsap/dist/MorphSVGPlugin';
import { ScrambleTextPlugin } from 'gsap/dist/ScrambleTextPlugin';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';
import { SplitText } from 'gsap/dist/SplitText';
import { ScrollItem } from './ScrollItem';
import { Effects } from './Effects';
import { throttle, debounce, isEmpty, find } from 'lodash';
import { version } from './version';
import Cursor from './Cursor';

export class Core {
  /**
   * @constructor
   * @class Core
   * @classdesc Animate elements on scroll with a viewport trigger
   *
   * @param {object} options
   *  @param {object} [options.scrollSmoother] View ScrollSmoother {@link https://greensock.com/docs/v3/Plugins/ScrollSmoother documentation}
   *  @param {bool} [options.scrollSmoother.active=false] If scrollSmoother should be enabled or not
   *  @param {number} [options.scrollSpeed=20] Number of pixels per scroll wheel tick)
   *  @param {string} [options.namespace=scroll] Namespace for all HTML data attributes
   *  @param {string} [options.matchMedia=all] Valid {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia matchMedia} string
   *  @param {string} [options.matchMedia=(min-width: 1200px) and (hover: hover)] Valid {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia matchMedia} string
   *  @param {number|bool} [options.smoothHorizontalScroll=1] Horizontal scroll smoothness - soften the link between the animation and the scrollbar
   *  @param {Cursor} [options.customCursor] View {@link Cursor}
   */
  constructor(options = {}) {
    const defaultOptions = {
      scrollSpeed: 20,
      namespace: 'scroll',
      matchMedia: 'all',
      matchMediaHorizontalScroll: '(min-width: 1200px) and (hover: hover)',
      smoothHorizontalScroll: 1,
      scrollSmoother: {
        active: false,
      },
      customCursor: {
        active: false,
      },
    };

    this.options = { ...defaultOptions, ...options };
    this.scrollSpeedTriggers = [];
    this.horizontalScrollContainers = [];
    this._scrollerEnabledListeners = [];
    this._contexts = [];
    this._version = version;
    this._effects = new Effects();
  }

  /**
   * Init
   */
  init() {
    gsap.registerPlugin(
      ScrollTrigger,
      ScrollSmoother,
      InertiaPlugin,
      MotionPathPlugin,
      MotionPathHelper,
      Physics2DPlugin,
      SplitText,
      MorphSVGPlugin,
      ScrambleTextPlugin,
      DrawSVGPlugin,
    );

    this._updateScrollSpeedItems = this.updateScrollSpeedItems.bind(this);
    this._throttledUpdateScrollSpeedItems = throttle(this._updateScrollSpeedItems, 100);

    this._bindGlobalListeners();
    this._setMotionState();
    this.addScrollSmoother(this.options.scrollSmoother);
    this._setScrollSpeedSectionsObserver();
    this._addScrollSpeedSections();
    this.addHorizontalScrollSections();
    this._setMatchMedia();
    this._addEffectsAnimations();
    this._addCustomCursor();
  }

  /**
   * Adds a custom cursor
   */
  _addCustomCursor() {
    if (this.isActive && this.options.customCursor?.active) {
      this.cursor = new Cursor(this.options.customCursor, this.options.scrollSmoother?.active);
    }
  }

  /**
   * Adds Scroll Smoother
   * @param {object} [options] View ScrollSmoother {@link https://greensock.com/docs/v3/Plugins/ScrollSmoother documentation}
   *  @param {bool} [options.active=false] If scrollSmoother should be enabled or not
   */
  addScrollSmoother(options = { active: false }) {
    const { active } = options;

    if (!active || !this.isActive) return;

    const defaultOptions = {
      smooth: 1,
      effects: true,
      normalizeScroll: true,
      ignoreMobileResize: true,
    };
    const scrollSmootherOptions = {
      ...defaultOptions,
      ...options,
    };

    this.options = { ...this.options, scrollSmoother: { ...scrollSmootherOptions } };
    this.ScrollSmoother = ScrollSmoother.create({
      ...options,
    });
  }

  /**
   * Binds global listeners
   */
  _bindGlobalListeners() {
    const _this = this;
    const { matchMedia } = this.options;
    const motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mediaQuery = window.matchMedia(matchMedia);

    window.addEventListener('resize', debounce(_this.resize.bind(_this), 200));
    document.addEventListener('_before_page_loaded', _this.reset.bind(_this));

    if (mediaQuery?.addEventListener) {
      motionMedia.addEventListener('change', _this._setMotionState.bind(_this));
      mediaQuery.addEventListener('change', _this._setMatchMedia.bind(_this));
    } else {
      mediaQuery.addListener(_this._setMatchMedia.bind(_this));
      motionMedia.addListener(_this._setMotionState.bind(_this));
    }
  }

  /**
   * Binds the scroll event to update the {@link ScrollItem} objects
   */
  bindScrollSpeed() {
    this.scrollSpeedActive = true;
    window.addEventListener('scroll', this._throttledUpdateScrollSpeedItems);

    this.scrollSpeedTriggers?.forEach((trigger) => {
      trigger.scrollSpeedItems?.forEach((item) => item.resize());
    });
  }

  /**
   * Unbinds the scroll event to update the {@link ScrollItem} objects
   */
  unbindScrollSpeed() {
    this.scrollSpeedActive = false;
    window.removeEventListener('scroll', this._throttledUpdateScrollSpeedItems);

    this.scrollSpeedTriggers?.forEach((trigger) => {
      trigger.scrollSpeedItems?.forEach((item) => item.reset());
    });
  }

  /**
   * Resets all scroll elements and observers
   */
  reset() {
    this.scrollSpeedTriggers = [];
    this._scrollSpeedTriggersObserver.disconnect();

    ScrollTrigger.getAll()?.forEach((instance) => instance.kill(false));
    ScrollTrigger.refresh();
    ScrollTrigger.clearScrollMemory();

    this._scrollerEnabledListeners?.forEach((listener) => document.removeEventListener('scrollerEnabled', listener));
    this._scrollerEnabledListeners = [];

    this._addScrollSpeedSections();
    this.addHorizontalScrollSections();
    this._addEffectsAnimations();
  }

  /**
   * Resets a specific timeline to its original state
   * @param {object} timeline {@link https://greensock.com/docs/v3/GSAP/Timeline GSAP Timeline}
   */
  _resetTimeline(timeline) {
    const { animation, trigger } = timeline;

    animation?.pause();
    animation?.progress(1);

    if (typeof animation?.getChildren === 'function') {
      animation.getChildren()?.forEach((element) => {
        gsap.set(element.targets(), { clearProps: 'all' });
      });
    }

    if (!animation) {
      gsap.set(trigger, { clearProps: 'all' });
    }
  }

  /**
   * Enables this module
   */
  enable() {
    this.isActive = true;

    ScrollTrigger.getAll()?.forEach((instance) => {
      const { animation, trigger } = instance;
      let hasPlayed;

      if (typeof animation?.getChildren === 'function') {
        hasPlayed = animation.getChildren()?.some((timeline) => timeline.ratio);
      }

      instance.enable();

      if (hasPlayed) animation.progress(1);
      if (!animation) {
        trigger.classList.remove('is-animated');
        ScrollTrigger.refresh();
      }
    });

    this.bindScrollSpeed();
    this.addScrollSmoother(this.options.scrollSmoother);
    this._addCustomCursor();

    document.dispatchEvent(new Event('scrollerEnabled'));
  }

  /**
   * Disables this module
   */
  disable() {
    const _this = this;
    this.isActive = false;

    ScrollTrigger.getAll()?.forEach((instance) => {
      instance.disable();
      _this._resetTimeline(instance);
    });

    this.unbindScrollSpeed();
    document.dispatchEvent(new Event('scrollerDisabled'));
  }

  /**
   * Updates elements on a window resize
   */
  resize() {
    if (!this.isActive) return;

    if (this.scrollSpeedActive) {
      this.scrollSpeedTriggers?.forEach((trigger) => {
        trigger.scrollSpeedItems?.forEach((item) => item.resize());
      });
    }
  }

  /**
   * Toggles scrollSpeed state on media change
   * (ScrollTrigger animations are handled by GSAP)
   * @param {MediaQueryListEvent|bool} mediaQuery {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryListEvent MediaQueryListEvent}
   */
  _setMatchMedia(mediaQuery = false) {
    const { matchMedia } = this.options;

    if (!mediaQuery) mediaQuery = window.matchMedia(matchMedia);

    if (mediaQuery.matches && this.isActive) this.bindScrollSpeed();
    else this.unbindScrollSpeed();
  }

  /**
   * Sets the motion state
   *  - Will either enable or disable all animations
   *  - Based on accessibility 'prefers-reduced-motion'
   * @param {MediaQueryListEvent|bool} mediaQuery {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryListEvent MediaQueryListEvent}
   */
  _setMotionState(mediaQuery = false) {
    if (!mediaQuery) mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isActive = !mediaQuery.matches;

    if (!this.isActive) this.disable();
    else this.enable();
  }

  /**
   * Sets the threshold ratios for IntersectionObserver
   * @param {number} max Max amount of thresholds to observe on an element
   */
  _setThresholdList(max = 20) {
    const thresholds = [];

    for (let i = 0; i <= max; i++) {
      const ratio = i / max;
      thresholds.push(ratio);
    }

    return thresholds;
  }

  /**
   * Sets the sections observer
   */
  _setScrollSpeedSectionsObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: this._setThresholdList(),
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in-view');
        } else {
          entry.target.classList.remove('is-in-view');
        }
      });
    };

    this._scrollSpeedTriggersObserver = new IntersectionObserver(callback, options);
  }

  /**
   * Updates all {@link ScrollItem} objects while their trigger is visible in the viewport
   */
  updateScrollSpeedItems() {
    if (!this.isActive) return;

    this.scrollSpeedTriggers?.forEach((trigger) => {
      if (trigger.el.classList.contains('is-in-view')) {
        trigger.scrollSpeedItems?.forEach((item) => item.update());
      }
    });
  }

  /**
   * Adds all scroll effects registered through data attributes
   * @param {string} [selector=[data-${this.options.namespace}-section]]
   */
  _addEffectsAnimations(selector = `[data-${this.options.namespace}-section]`) {
    const { namespace } = this.options;
    const triggers = gsap.utils.toArray(selector);

    triggers?.forEach((triggerElement) => {
      const effects = [...triggerElement.querySelectorAll(`[data-${namespace}-effect`)].reduce(
        (set, element) => set.add(element.getAttribute(`data-${namespace}-effect`)),
        new Set(),
      );

      effects?.forEach((effect) => {
        const elements = [...triggerElement.querySelectorAll(`[data-${namespace}-effect="${effect}"]`)];
        const horizontalContainer = this.horizontalScrollContainers.find((container) =>
          elements.some((element) => container.el.contains(element)),
        );

        this.addAnim({
          element: elements,
          trigger: triggerElement,
          timeline: [
            {
              effect,
              to: { stagger: 0.1 },
            },
          ],
          batch: true,
          horizontal: !!horizontalContainer,
        });
      });
    });
  }

  /**
   * Adds scroll speed sections and assigns their children
   * @param {string} [selector=[data-${this.options.namespace}-section]]
   */
  _addScrollSpeedSections(selector = `[data-${this.options.namespace}-section]`) {
    const elements = gsap.utils.toArray(selector);
    const { namespace } = this.options;

    elements?.forEach((element) => {
      const foundTrigger = this.scrollSpeedTriggers.find((speedTrigger) => speedTrigger.el === element);

      if (foundTrigger) {
        const { scrollSpeedItems } = foundTrigger;

        foundTrigger.scrollSpeedItems = [...scrollSpeedItems, ...this._addScrollSpeedItems(element, foundTrigger)];

        return;
      }

      element.setAttribute(`data-${namespace}-section`, '');

      this.scrollSpeedTriggers.push({
        el: element,
        scrollSpeedItems: this._addScrollSpeedItems(element),
      });

      this._scrollSpeedTriggersObserver.observe(element);
    });
  }

  /**
   * Adds the elements that are going to use a different scroll speed
   *
   * @requires ScrollItem class
   * @param {HTMLElement} trigger Parent element section for scroll speed items
   * @returns {ScrollItem[]} Array of {@link ScrollItem}
   */
  _addScrollSpeedItems(trigger) {
    const { namespace, scrollSpeed } = this.options;
    const foundTrigger = this.scrollSpeedTriggers.find((speedTrigger) => speedTrigger.el === trigger);
    const scrollElements = trigger.querySelectorAll(`[data-${namespace}-speed]`);
    const scrollSpeedItems = [];

    scrollElements?.forEach((element) => {
      if (foundTrigger) {
        if (foundTrigger.scrollSpeedItems.some((speedTrigger) => speedTrigger.el === element)) return;
      }

      const scrollItem = new ScrollItem(element, {
        globalSpeed: scrollSpeed,
        speed: element.getAttribute(`data-${namespace}-speed`),
      });

      scrollItem.update(0);
      scrollSpeedItems.push(scrollItem);
    });

    return scrollSpeedItems;
  }

  /**
   * Adds horizontal scroll container sections and reference
   * @param {string} [selector=[data-${this.options.namespace}-horizontal-container]]
   */
  async addHorizontalScrollSections(selector = `[data-${this.options.namespace}-horizontal-container]`) {
    const elements = gsap.utils.toArray(selector);
    const { namespace, matchMediaHorizontalScroll, smoothHorizontalScroll } = this.options;

    elements?.forEach((container) => {
      const sections = [...container.querySelectorAll(`[data-${namespace}-horizontal-section]`)];

      if (!isEmpty(sections)) {
        gsap.matchMedia().add(matchMediaHorizontalScroll, () => {
          this.horizontalScrollContainers.push({
            el: container,
            tween: gsap.to(sections, {
              x: () => {
                return -container.scrollWidth + window.innerWidth;
              },
              ease: 'none',
              scrollTrigger: {
                trigger: container,
                start: 'top top',
                pin: true,
                scrub: smoothHorizontalScroll,
                invalidateOnRefresh: true,
                end: () => `+=${container.scrollWidth - window.innerWidth}`,
                pinSpacer: container.closest(`[data-${namespace}-horizontal-pin]`),
              },
            }),
          });
        });
      }
    });
  }

  /**
   *
   * @typedef {object} options
   *  @property {string|HTMLElement|NodeList} element Selector
   *  @property {string|HTMLElement} trigger Defines the reference section for scroll speed and batched animations
   *  @property {number} [scrollSpeed] Speed multiplied by the global scrollSpeed value of {@link ScrollAnimController}
   *  @property {bool} [batch] If animations should be batched. Allows staggering of elements as they enter viewport.
   *  @property {bool} [horizontal] If animations should be for horizontal scroll only.
   *  @property {callback} [advanced] Callback for advanced GSAP animations
   *  @property {object} [scrollTrigger] View ScrollTrigger {@link https://greensock.com/docs/v3/Plugins/ScrollTrigger documentation}
   *  @property {string|number|function} [scrollTrigger.start=30% bottom] By default the animation will start when the element is 30%
   *                                                                      inside of the viewport unless <code>'scrub: true'</code> is
   *                                                                      set in which case the default value is <code>'top bottom'</code>
   *  @property {animation[]} [timeline] Array of {@link animation} objects
   */

  /**
   *
   * @typedef {object} animation
   *  @property {string} [label] Label to group animations and save as a reference point on the timeline
   *  @property {object} [to] Properties to animate {@link https://greensock.com/docs/v3/GSAP/gsap.to() documentation}
   *  @property {object} [set] Properties to animate {@link https://greensock.com/docs/v3/GSAP/gsap.set() documentation}
   */

  /**
   * Adds a timeline for {@link https://greensock.com/docs/v3/Plugins/ScrollTrigger ScrollTrigger} animations
   * TODO: Review this whole thing
   *       Simplify / adapt code to reduce duplicated lines
   *       Keep an easy way and a more advanced mechanic?
   * @param {options} options {@link options}
   */
  _addTimeline(options) {
    const { element, trigger, timeline, scrollTrigger = {}, batch, batchWithScrub, horizontal } = options;
    const { effects } = this._effects;
    const start = scrollTrigger?.scrub ? 'top bottom' : 'top 80%';
    const batchIncompatibilities = [
      'animation',
      'invalidateOnRefresh',
      'onSnapComplete',
      'onScrubComplete',
      'scrub',
      'snap',
      'toggleActions',
      'trigger',
    ];

    let scrollTriggerDefaults = { start };
    if (horizontal) {
      scrollTriggerDefaults = {
        start: 'left 80%',
        horizontal: true,
      };
    }

    if (batch && scrollTrigger) {
      const incompatibilities = batchIncompatibilities.filter((prop) => prop in scrollTrigger);

      if (!isEmpty(incompatibilities)) {
        throw new Error(`Batching is incompatible with the following properties: ${incompatibilities.join(', ')}.`);
      }
    }

    const elements = gsap.utils.toArray(element);
    const triggers = gsap.utils.toArray(trigger);

    triggers?.forEach((triggerElement) => {
      if (!triggerElement) return;

      const batchedElements = elements.filter((element) => triggerElement.contains(element));
      const horizontalContainer = this.horizontalScrollContainers.find((container) =>
        batchedElements.some((element) => container.el.contains(element)),
      );

      if ((horizontal && isEmpty(horizontalContainer)) || (!horizontal && !isEmpty(horizontalContainer))) return;
      if (horizontal && !isEmpty(horizontalContainer)) scrollTrigger.containerAnimation = horizontalContainer.tween;

      const scrollTriggerFinal = { ...scrollTriggerDefaults, ...scrollTrigger };

      if (batch) {
        ScrollTrigger.batch(batchedElements, {
          ...scrollTriggerFinal,
          onEnter: (batch) => {
            if (horizontal) batch.reverse();
            if (batch.some((element) => element.classList.contains('is-animated'))) return;

            const tl = gsap.timeline({
              onComplete: () => batch?.forEach((element) => element.classList.add('is-animated')),
            });

            timeline?.forEach((anim) => {
              const { label, to, effect } = anim;
              const type = effect || 'to';

              if (effect && !effects.some((effectElement) => effectElement.id === effect))
                throw new Error(`${effect} is not a registered effect.`);

              tl[type](batch, { ...to }, label);
            });
          },
        });

        timeline?.forEach((anim) => {
          const { set = {}, effect } = anim;
          const effectSet = {};

          if (effect) {
            const effectData = effects.find((effectElement) => effectElement.id === effect);
            Object.assign(effectSet, effectData.set);
          }

          gsap.set(elements, { ...set, ...effectSet });

          const listener = () => gsap.set(elements, { ...set, ...effectSet });
          document.addEventListener('scrollerEnabled', listener);
          this._scrollerEnabledListeners.push(listener);
        });
      } else {
        if (batchWithScrub) {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger,
              ...scrollTriggerFinal,
            },
          });

          timeline?.forEach((anim) => {
            const { label, to, set, effect } = anim;
            const type = effect || 'to';

            if (set) gsap.set(element, { ...set });
            if (effect && !effects.some((effectElement) => effectElement.id === effect))
              throw new Error(`${effect} is not a registered effect.`);

            tl[type](element, { ...to }, label);
          });
        } else {
          batchedElements?.forEach((element) => {
            const tl = gsap.timeline({
              scrollTrigger: {
                trigger: element,
                ...scrollTriggerFinal,
              },
              onComplete: () => element.classList.add('is-animated'),
            });

            timeline?.forEach((anim) => {
              const { label, to, set, effect } = anim;
              const type = effect || 'to';

              if (set) gsap.set(element, { ...set });
              if (effect && !effects.some((effectElement) => effectElement.id === effect))
                throw new Error(`${effect} is not a registered effect.`);

              tl[type](element, { ...to }, label);
            });
          });
        }
      }
    });

    if (!this.isActive) this.disable();
  }

  /**
   * Adds our timelines through a valid {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia matchMedia} rule
   * @param {options} options {@link options}
   * @param {string|HTMLElement|Object} scope The scope can be selector text itself like ".myClass", or an Element or React Ref
   */
  _addMatchMedia(options, scope) {
    const _this = this;
    const { matchMedia } = this.options;

    if (matchMedia) {
      const { element, advanced } = options;

      if (scope) {
        const existingScope = find(this._contexts, ({ scope: s }) => s === scope);

        if (existingScope) {
          const scrollerMatchMedia = existingScope.add(matchMedia, () => {
            if (advanced) advanced();
            else _this._addTimeline(options);
          });

          return scrollerMatchMedia;
        }
      }

      ScrollTrigger.saveStyles(element);
      const scrollerMatchMedia = gsap.matchMedia(scope).add(matchMedia, () => {
        if (advanced) advanced();
        else _this._addTimeline(options);
      });

      this._contexts.push(scrollerMatchMedia);

      return scrollerMatchMedia;
    }
  }

  /**
   * Reverts scope and removes it from contexts array
   * @param {string|HTMLElement|Object} scope The scope can be selector text itself like ".myClass", or an Element or React Ref
   */
  revertScope(scope) {
    const existingScope = find(this._contexts, ({ scope: s }) => s === scope);

    if (!isEmpty(existingScope)) {
      existingScope.revert();
      this._contexts = this._contexts.filter((s) => s !== existingScope);
    }
  }

  /**
   * Adds a custom animation
   * @param {options} options {@link options}
   * @param {string|HTMLElement|Object} scope The scope can be selector text itself like ".myClass", or an Element or React Ref
   */
  addAnim(options = {}, scope) {
    const defaultOptions = {
      scrollSpeed: 0,
    };

    options = { ...defaultOptions, ...options };

    const { trigger, advanced } = options;

    if (!trigger && !advanced) throw { error: 'No trigger defined for animation.', animation: options };

    this._addScrollSpeedAnim(options);
    return this._addMatchMedia(options, scope);
  }

  /**
   * Adds scroll speed animations via custom added animation
   * @param {options} options {@link options}
   */
  _addScrollSpeedAnim(options) {
    const { namespace } = this.options;
    const { element, scrollSpeed, trigger } = options;

    if (!scrollSpeed) return;

    const elements = gsap.utils.toArray(element);
    const triggers = gsap.utils.toArray(trigger);

    triggers?.forEach((triggerElement) => {
      const batchedElements = elements.filter((element) => triggerElement.contains(element));

      batchedElements?.forEach((element) => element.setAttribute(`data-${namespace}-speed`, scrollSpeed));
      this._addScrollSpeedSections(triggers);
    });
  }

  // End
}
